import { Node as RFNode } from "reactflow";
import type { FunctionTemplatePayload, PackageTemplatePayload, PackagePayload } from "../services/packages/types";
import { NodeDescriptor } from "../registry/types";
import { NodeTemplateId } from "../registry/types";
import { tryGetNodeDescriptor } from "../registry/nodeRegistry";
import { BUILTIN_PACKAGE_ID } from "../registry/packageKeys";
import { getFlowNodeCanonicalType } from "./flowNodeCanonicalType";
import { nodeDisplayLabel } from "./nodeDisplayLabel";
import { normalizePortTypes } from "../constants/supportedPortTypes";
import { normalizeWidgets } from "./widgets/widgetModel";
import {
  applyCanvasTemplateConfigToTemplateDraft,
  readCanvasTemplateConfig,
} from "./canvasTemplateConfig";
import {
  Draft,
  TemplateDraft,
  Category,
  Engine,
  makeDraft,
  factoryUiMakeId,
  STARTER_CODE,
  toApiPayload,
} from "../services/packages/factoryDraft";

/** Matches `PackageId` segment rules (`PACKAGE_DIR_RE` in `utk_curio.backend.app.packages.domain.package_id`). */
const PACK_ID_SEGMENT_MAX_LEN = 63;

/** Mirrors backend `KIND_ID_RE` (`utk_curio.backend.app.packages.domain.package_id`). */
const TEMPLATE_ID_SEGMENT_MAX_LEN = 63;

const WIZARD_CATEGORIES = new Set<string>(["data", "computation", "vis_grammar", "vis_simple", "flow"]);

export type StartersLookup = (type: NodeTemplateId, custom: boolean) => readonly { name: string; code: string }[];

/**
 * Normalizes arbitrary input into a single valid reverse‑DNS trailing segment (`[a-z][a-z0-9-]{0,62}`).
 * UUID hex without hyphens often starts with `0‑9`; those are prefixed so factory install validates.
 */
export function normalizePackageIdLeaf(raw: string): string {
  let s = String(raw ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, "");
  s = s.replace(/-{2,}/g, "-").replace(/^-+/, "").replace(/-+$/, "");
  if (!s.length) {
    s = "palette";
  }
  if (!/^[a-z]/.test(s)) {
    s = `d${s}`;
  }
  if (s.length > PACK_ID_SEGMENT_MAX_LEN) {
    s = s.slice(0, PACK_ID_SEGMENT_MAX_LEN).replace(/-+$/, "");
  }
  if (!s.length || !/^[a-z]/.test(s)) {
    s = "draft";
  }
  return s;
}

export type FactoryInstallEnvelope = Record<string, unknown>;

/**
 * Payload for POST `/api/packages/factory/install`.
 * When `replace` is true the backend **replaces the installed package directory** (`packageId@major`)
 * so the **Packages palette** picks up the new manifest. It does **not** modify nodes on the canvas.
 */
export function buildFactoryInstallEnvelope(draft: Draft, replace?: boolean): FactoryInstallEnvelope {
  const payload = toApiPayload(draft);
  if (replace === undefined || !replace) return { ...payload };
  return { ...payload, replace: true };
}

export function runtimeCodeFromRfNode(node: RFNode<any>): string {
  const d = node?.data ?? {};
  if (typeof (d as { code?: unknown }).code === "string" && ((d as { code: string }).code as string).trim()) {
    return (d as { code: string }).code;
  }
  if (
    typeof (d as { defaultCode?: unknown }).defaultCode === "string" &&
    ((d as { defaultCode: string }).defaultCode as string).trim()
  ) {
    return (d as { defaultCode: string }).defaultCode as string;
  }
  return STARTER_CODE;
}

export function canonicalTemplateSlugForDescriptor(desc: NodeDescriptor): string {
  const id = String(desc.id);
  const m = id.match(/^[^/]+\/([^/@]+)@\d+$/);
  if (m) return m[1];
  return id.toLowerCase().replace(/_/g, "-");
}

function inferSourceFilename(packageRelativePath: string | undefined, templateId: string): string {
  if (packageRelativePath) {
    const parts = packageRelativePath.split("/").filter(Boolean);
    const last = parts[parts.length - 1];
    if (last) return last;
  }
  // Per kind, never a shared "default.py". A canvas node has no
  // package-relative source, so every node saved out of the canvas lands here:
  // with a constant fallback, saving a second node into the same package wrote
  // over the first kind's source (`toApiPayload` maps each kind to
  // `sources/<sourceFilename>`, so the later save simply won). The manifest kept
  // both kinds, which made it invisible until you ran them - the palette showed
  // two rows executing identical code. `templateId` already carries the
  // collision suffix that keeps two same-typed nodes apart, and this mirrors the
  // wizard's own fallback in factoryDraftModel.
  return `${templateId}.py`;
}

function deriveEngine(desc: NodeDescriptor): Engine {
  if (desc.editor === "grammar") return "javascript";
  return "python";
}

function ensurePorts(desc: NodeDescriptor): { inP: TemplateDraft["inputPorts"]; outP: TemplateDraft["outputPorts"] } {
  const inputPorts =
    desc.inputPorts.length === 0
      ? []
      : desc.inputPorts.map((p) => ({
          id: factoryUiMakeId(),
          types: [...p.types],
          cardinality: String(p.cardinality ?? "1"),
        }));
  const outputPortsRaw =
    desc.outputPorts.length === 0
      ? [{ id: factoryUiMakeId(), types: ["JSON"], cardinality: "1" }]
      : desc.outputPorts.map((p) => ({
          id: factoryUiMakeId(),
          types: [...p.types],
          cardinality: String(p.cardinality ?? "1"),
        }));
  return { inP: inputPorts, outP: outputPortsRaw };
}

/** Maps manifest default template paths to dropdown names (see `packageNodeBehavior.tsx`). */
function sourceDisplayName(sourcePath: string | undefined): string | undefined {
  if (!sourcePath) return undefined;
  const basename = sourcePath.split("/").pop() ?? "";
  if (!basename) return undefined;
  const stem = basename.replace(/\.[^.]+$/u, "");
  return stem.replace(/_/g, " ");
}

/** Unique slug within `occupied` (`[a-z][a-z0-9-]…`). */
function forkTemplateSlugAwayFrom(baseSlug: string, occupied: ReadonlyMap<string, unknown>): string {
  for (let n = 0; n < 64; n++) {
    const leaf = normalizePackageIdLeaf(`${factoryUiMakeId()}${n}${baseSlug}`);
    let cand = `${baseSlug}-k-${leaf}`;
    if (cand.length > TEMPLATE_ID_SEGMENT_MAX_LEN) {
      cand = cand.slice(0, TEMPLATE_ID_SEGMENT_MAX_LEN).replace(/-+$/u, "");
    }
    if (cand && /^[a-z][a-z0-9-]*$/u.test(cand) && !occupied.has(cand)) return cand;
  }
  const fallback = `${baseSlug}-k-${normalizePackageIdLeaf(factoryUiMakeId())}`;
  return fallback.length > TEMPLATE_ID_SEGMENT_MAX_LEN
    ? fallback.slice(0, TEMPLATE_ID_SEGMENT_MAX_LEN).replace(/-+$/u, "")
    : fallback;
}

export function descriptorToTemplateDraft(
  desc: NodeDescriptor,
  code: string,
  kindIdOverride?: string,
  labelOverride?: string,
): TemplateDraft {
  const templateId = kindIdOverride ?? canonicalTemplateSlugForDescriptor(desc);
  const sourceFilename = inferSourceFilename(desc.package?.source, templateId);

  const { inP, outP } = ensurePorts(desc);

  return {
    id: templateId,
    label: labelOverride?.trim() || desc.label || templateId,
    category: desc.category as Category,
    engine: deriveEngine(desc),
    editor: desc.editor,
    description: desc.description ?? "",
    hasCode: !!desc.hasCode,
    hasWidgets: !!desc.hasWidgets,
    hasGrammar: !!desc.hasGrammar,
    inputPorts: inP,
    outputPorts: outP,
    sourceFilename,
    sourceCode: code?.trim()?.length ? code : STARTER_CODE,
    // A copy of a node that loads a dataset loads it too (`loadsData`).
    ...(desc.loadsDataset ? { loadsDataset: true } : {}),
  };
}

export function normalizeTemplateLabel(label: string): string {
  return label.trim().toLowerCase();
}

/** Canvas-local template title; falls back to registry descriptor label. */
export function canvasTemplateLabelFromNode(node: { data: object }, desc: NodeDescriptor): string {
  const raw = (node.data as { packageTemplateLabel?: unknown })?.packageTemplateLabel;
  if (typeof raw === "string" && raw.trim()) return raw.trim();
  return desc.label || canonicalTemplateSlugForDescriptor(desc);
}

/**
 * The node's user-facing display name — the same label shown in its canvas
 * header. Single source of truth so the node header and the computed-dataset
 * title it produces never drift apart.
 *
 * The node's renamed header (``packageTemplateLabel``), else its registered
 * template's label, else its type in words without the version; a purely
 * numeric ``nodeType`` is a node id, named by its header or itself. The rule is
 * ``nodeDisplayLabel``, which the backend twins, so a run on the server and a
 * save name a node's computed dataset the same way (#775).
 */
export function resolveNodeDisplayLabel(
  data: { nodeType: NodeTemplateId; packageTemplateLabel?: string | null },
): string {
  const desc = tryGetNodeDescriptor(data.nodeType);
  return nodeDisplayLabel(data.nodeType, data.packageTemplateLabel, desc?.label);
}

/**
 * The title to persist for a computed dataset when (re)installing it, resolved
 * client-side from the producing node's type so the node name survives
 * publish → uninstall → reinstall (the original manifest is gone by then). The
 * backend uses it with priority over the filename-derived session title.
 *
 * Returns ``undefined`` (send nothing, let the backend fall back) for
 * non-computed datasets, when no producer node type is known, or when the type
 * is a purely numeric id with no node title available on the dataset item.
 */
export function resolveComputedInstallTitle(
  dataset: { origin?: string | null; producerNodeType?: string | null },
): string | undefined {
  if (dataset.origin !== "computed" || !dataset.producerNodeType) return undefined;
  const nodeType = String(dataset.producerNodeType).trim();
  if (!nodeType || /^\d+$/.test(nodeType)) return undefined;
  const label = resolveNodeDisplayLabel({ nodeType: nodeType as NodeTemplateId }).trim();
  return label || undefined;
}

function templateDraftFromCanvasNode(
  node: RFNode<any>,
  desc: NodeDescriptor,
  body: string,
  kindIdOverride?: string,
): TemplateDraft {
  const label = canvasTemplateLabelFromNode(node, desc);
  const base = descriptorToTemplateDraft(desc, body, kindIdOverride, label);
  const config = readCanvasTemplateConfig(node);
  const draft = applyCanvasTemplateConfigToTemplateDraft(base, config, label);
  // #662: the node's widgets travel with its code, so the package's source
  // resolves its references. Set values become the template's defaults.
  const widgets = normalizeWidgets(node.data?.widgets).map(({ value, ...w }) =>
    value !== undefined ? { ...w, default: value } : w,
  );
  return widgets.length > 0 ? { ...draft, widgets } : draft;
}

function categoryFromPackageTemplate(cat: string): Category {
  return WIZARD_CATEGORIES.has(cat) ? (cat as Category) : "computation";
}

function depsRows(record: Record<string, string>): { id: string; pkg: string; range: string }[] {
  return Object.entries(record).map(([pkg, range]) => ({
    id: factoryUiMakeId(),
    pkg,
    range: range ?? "*",
  }));
}

function seedCodeForPackageTemplatePayload(template: PackageTemplatePayload, getStarters?: StartersLookup): string {
  if (!getStarters) return STARTER_CODE;
  const starters = [...getStarters(template.id as NodeTemplateId, false)];
  const wanted = sourceDisplayName(template.source ?? undefined);
  const hit = wanted ? starters.find((t) => t.name === wanted) : starters[0];
  const body = typeof hit?.code === "string" ? hit.code : "";
  return body.trim().length ? body : STARTER_CODE;
}

function packageTemplatePayloadToTemplateDraft(template: PackageTemplatePayload, getStarters?: StartersLookup): TemplateDraft {
  // Empty for a template that ships no source (a behavior bundle drives it):
  // `toApiPayload` then sends it without one, as it is installed (#432).
  const sourceFilename = template.source?.split("/").pop()?.trim() ?? "";
  const inputPorts =
    template.inputPorts?.map((p) => ({
      id: factoryUiMakeId(),
      types: normalizePortTypes(p.types),
      cardinality: String(p.cardinality ?? "1"),
    })) ?? [];
  const outputPortsRaw =
    template.outputPorts?.length ?
      template.outputPorts.map((p) => ({
        id: factoryUiMakeId(),
        types: normalizePortTypes(p.types),
        cardinality: String(p.cardinality ?? "1"),
      }))
    : [{ id: factoryUiMakeId(), types: ["JSON"], cardinality: "1" }];
  const engine: Engine = template.engine === "javascript" ? "javascript" : "python";
  return {
    id: template.templateId,
    label: template.label || template.templateId,
    category: categoryFromPackageTemplate(template.category),
    engine,
    editor: template.editor,
    description: template.description ?? "",
    hasCode: !!template.hasCode,
    hasWidgets: !!template.hasWidgets,
    hasGrammar: !!template.hasGrammar,
    inputPorts,
    outputPorts: outputPortsRaw,
    behavior: template.behavior ?? undefined,
    iconRef: template.iconRef ?? undefined,
    paletteOrder: typeof template.paletteOrder === "number" ? template.paletteOrder : undefined,
    sourceFilename,
    sourceCode: seedCodeForPackageTemplatePayload(template, getStarters),
    // #662: a Save As keeps the widgets the existing template declares.
    ...(normalizeWidgets(template.widgets).length > 0 ? { widgets: normalizeWidgets(template.widgets) } : {}),
    ...(template.loadsDataset ? { loadsDataset: true } : {}),
  };
}

/** Factory draft from a ``GET /api/packages`` row: the base Save As adds a node to. */
export function draftFromInstalledPackagePayload(
  pkg: PackagePayload,
  getStarters?: StartersLookup,
): Draft {
  const d = makeDraft();
  d.packageId = pkg.packageId;
  d.major = pkg.major;
  d.version = pkg.version;
  d.name = pkg.name;
  d.publisher = pkg.publisher;
  d.description = pkg.description;
  d.license = pkg.license ?? "MIT";
  d.permissions = [...pkg.permissions];
  d.pythonDeps = depsRows(pkg.dependencies.python ?? {});
  d.jsDeps = depsRows(pkg.dependencies.js ?? {});
  d.packageDeps = depsRows(pkg.dependencies.packages ?? {});
  d.lineage = pkg.lineage
    ? {
        forkedFrom: {
          packageId: pkg.lineage.forkedFrom.packageId,
          major: pkg.lineage.forkedFrom.major,
        },
        root: {
          packageId: pkg.lineage.root.packageId,
          major: pkg.lineage.root.major,
        },
      }
    : null;
  d.templates = pkg.templates.map((k) => packageTemplatePayloadToTemplateDraft(k, getStarters));
  if (typeof pkg.createdAt === "string" && pkg.createdAt.trim()) {
    d.createdAt = pkg.createdAt.trim();
  }
  return d;
}

export const SAVE_AS_NEW_PACK = "__save_as_new__";

/** True when Save As into `package` would overwrite an existing kind with the same label. */
export function saveAsWouldReplaceByLabel(pkg: PackagePayload, nodeLabel: string): boolean {
  const norm = normalizeTemplateLabel(nodeLabel);
  if (!norm) return false;
  return pkg.templates.some((k) => normalizeTemplateLabel(k.label) === norm);
}

/** Where Save As, or New node from a Python function, writes its template. */
export type TemplateSaveTarget =
  | { kind: "new"; packageDisplayName?: string }
  | { kind: "installed"; package: PackagePayload };

/**
 * The factory install draft that saves ONE template into *target*: a new
 * package holding just it, or an installed package it joins. In an installed
 * package a template of the same label is replaced, keeping its id; otherwise
 * the template takes *slugBase*, forked away from an id already taken.
 * *makeTemplate* builds the template for the id it ends up with. Shared by
 * Save as package node and New node from a Python function.
 */
export function buildTemplateInstallDraft(opts: {
  label: string;
  slugBase: string;
  makeTemplate: (templateId: string) => TemplateDraft;
  target: TemplateSaveTarget;
  getStarters?: StartersLookup;
  /** A new package's description, and the package it forks, if any. */
  newPackage: { description: string; lineage?: Draft["lineage"] };
}): Draft {
  const { label, slugBase, makeTemplate } = opts;
  if (opts.target.kind === "new") {
    const leaf = normalizePackageIdLeaf(factoryUiMakeId());
    const draft = makeDraft();
    draft.packageId = `curio.canvas.draft.${leaf}`;
    draft.name = opts.target.packageDisplayName?.trim() || `${label} package`;
    draft.publisher = "Local palette";
    draft.description = opts.newPackage.description;
    draft.templates = [makeTemplate(slugBase)];
    draft.lineage = opts.newPackage.lineage ?? null;
    return draft;
  }

  const draft = draftFromInstalledPackagePayload(opts.target.package, opts.getStarters);

  const labelMatchIdx = draft.templates.findIndex(
    (k) => normalizeTemplateLabel(k.label) === normalizeTemplateLabel(label),
  );
  if (labelMatchIdx >= 0) {
    draft.templates[labelMatchIdx] = makeTemplate(draft.templates[labelMatchIdx]!.id);
    return draft;
  }

  const kindsMap = new Map(draft.templates.map((k) => [k.id, k]));
  const templateId = kindsMap.has(slugBase) ? forkTemplateSlugAwayFrom(slugBase, kindsMap) : slugBase;
  draft.templates.push(makeTemplate(templateId));
  return draft;
}

/** Build a factory install draft when saving a single canvas node into a package (Save As). */
export function buildSaveAsInstallDraft(opts: {
  canvasNode: RFNode<any>;
  target: TemplateSaveTarget;
  getStarters?: StartersLookup;
}): Draft | null {
  const nt = getFlowNodeCanonicalType(opts.canvasNode as RFNode);
  if (!nt) return null;
  const desc = tryGetNodeDescriptor(nt as NodeTemplateId);
  if (!desc) return null;

  const label = canvasTemplateLabelFromNode(opts.canvasNode, desc);
  const body = runtimeCodeFromRfNode(opts.canvasNode);

  // A node of an installed package makes a new package a fork of it, so the
  // Node Catalog groups the two. A built-in node forks nothing.
  let lineage: Draft["lineage"] = null;
  const src = desc.package;
  if (src && src.packageId !== BUILTIN_PACKAGE_ID) {
    const from = { packageId: src.packageId, major: src.major };
    lineage = {
      forkedFrom: from,
      root: src.lineage?.root
        ? { packageId: src.lineage.root.packageId, major: src.lineage.root.major }
        : from,
    };
  }
  return buildTemplateInstallDraft({
    label,
    slugBase: canonicalTemplateSlugForDescriptor(desc),
    makeTemplate: (templateId) => templateDraftFromCanvasNode(opts.canvasNode, desc, body, templateId),
    target: opts.target,
    getStarters: opts.getStarters,
    newPackage: { description: "Created from canvas Save As.", lineage },
  });
}

/**
 * The template draft of a node that calls a Python function, from the
 * template the backend wrote (`packagesApi.functionTemplate`), under the id
 * {@link buildTemplateInstallDraft} gives it.
 */
export function functionTemplateDraft(written: FunctionTemplatePayload, templateId: string): TemplateDraft {
  const t = written.template;
  const ports = (list: { types: string[]; cardinality: string }[]) =>
    list.map((p) => ({ id: factoryUiMakeId(), types: normalizePortTypes(p.types), cardinality: p.cardinality }));
  const widgets = normalizeWidgets(t.widgets);
  return {
    id: templateId,
    label: t.label,
    category: categoryFromPackageTemplate(t.category),
    engine: "python",
    editor: "code",
    description: t.description,
    hasCode: t.hasCode,
    hasWidgets: t.hasWidgets,
    hasGrammar: t.hasGrammar,
    inputPorts: ports(t.inputPorts),
    outputPorts: ports(t.outputPorts),
    behavior: t.behavior,
    iconRef: t.iconRef,
    sourceFilename: `${templateId}.py`,
    sourceCode: written.source.code,
    ...(widgets.length > 0 ? { widgets } : {}),
  };
}

/**
 * The factory install draft that adds the template of a node calling a Python
 * function to *target*. A template outside the function's own package
 * depends on that package, which hands its node the function's module.
 */
export function buildFunctionInstallDraft(opts: {
  written: FunctionTemplatePayload;
  target: TemplateSaveTarget;
  getStarters?: StartersLookup;
}): Draft {
  const { written } = opts;
  const draft = buildTemplateInstallDraft({
    label: written.template.label,
    slugBase: written.template.id,
    makeTemplate: (templateId) => functionTemplateDraft(written, templateId),
    target: opts.target,
    getStarters: opts.getStarters,
    newPackage: { description: "Nodes made from Python functions." },
  });
  const own =
    opts.target.kind === "installed" &&
    `${opts.target.package.packageId}@${opts.target.package.major}` === written.package.dirName;
  if (!own) {
    for (const [pkg, range] of Object.entries(written.dependency)) {
      if (!draft.packageDeps.some((d) => d.pkg === pkg)) {
        draft.packageDeps.push({ id: factoryUiMakeId(), pkg, range });
      }
    }
  }
  return draft;
}
